import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { formatValue } from '../../../../utils';
// eslint-disable-next-line import/no-cycle
import { Commands } from '..';
import * as s from './styled-list';

// Lucide-style inline icons (match exato do design Operacional.html).
const ISearch = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);
const ILock = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);
// lucide:lock-open — estado "armado" (comandos liberados)
const ILockOpen = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 7.5-2" />
  </svg>
);
// lucide:history — abre o modal de historico de comandos
const IHistory = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 12a9 9 0 1 0 3-7.7L3 7" />
    <path d="M3 3v4h4" />
    <path d="M12 7v5l3 2" />
  </svg>
);

const List = ({
  variables,
  values,
  setPlacePin,
  highlightVar,
  setHighlightVar,
  pins,
  thing,
}) => {
  const { user } = useSelector((state) => state.usuario);
  // Estado elevado do Commands: a trava (locked) e o modal de historico
  // agora sao acionados pelos icones do RailHead (que ja estavam
  // desenhados mas eram decorativos / sem handler). Commands consome
  // esses props e nao tem mais header proprio — evita o cadeado/historico
  // duplicado que existia antes (um inerte no topo, um funcional embaixo).
  const isViewer = user.role === 'visualizador';
  const [locked, setLocked] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Abas do rail: 'variaveis' | 'comandos'. Viewer nao tem comandos, entao
  // fica so em variaveis (sem aba de comandos).
  const [activeTab, setActiveTab] = useState('variaveis');
  const showCommandsTab = !isViewer && activeTab === 'comandos';

  const handleAddVariable = (item) => {
    const ids = pins.map(({ variable }) => variable.id);
    if (!ids.includes(item.id)) {
      setHighlightVar(false);
      setPlacePin(item);
    } else if (highlightVar === item.id) {
      setHighlightVar(false);
    } else {
      setHighlightVar(item.id);
    }
  };

  // Determina o estilo do valor baseado no numero:
  //   - "neg" se o valor texto comeca com - (vermelho)
  //   - "zero" se o valor formatado vira 0/0,00 (mute)
  //   - default = cyan
  const styleFor = (raw) => {
    const txt = String(formatValue(raw));
    if (txt.startsWith('-') || txt.startsWith('−')) return 'neg';
    if (/^0([,.]0+)?$/.test(txt.replace(/\s/g, ''))) return 'zero';
    return '';
  };

  return (
    <s.Container>
      <s.RailHead>
        {isViewer ? (
          <s.RailHeadChip>Variáveis</s.RailHeadChip>
        ) : (
          <s.RailTabs role="tablist">
            <s.RailTab
              type="button"
              role="tab"
              aria-selected={activeTab === 'variaveis'}
              $active={activeTab === 'variaveis'}
              onClick={() => setActiveTab('variaveis')}
            >
              Variáveis
            </s.RailTab>
            <s.RailTab
              type="button"
              role="tab"
              aria-selected={activeTab === 'comandos'}
              $active={activeTab === 'comandos'}
              onClick={() => setActiveTab('comandos')}
            >
              Comandos
            </s.RailTab>
          </s.RailTabs>
        )}

        {/* Icones de comando (trava/historico) so na aba Comandos */}
        {showCommandsTab && (
          <s.RailHeadIcons>
            <s.RailHeadIconBtn
              type="button"
              $armed={!locked}
              aria-pressed={!locked}
              aria-label={locked ? 'Desbloquear comandos' : 'Bloquear comandos'}
              title={locked ? 'Desbloquear comandos' : 'Bloquear comandos'}
              onClick={() => setLocked((v) => !v)}
            >
              {locked ? ILock : ILockOpen}
            </s.RailHeadIconBtn>
            <s.RailHeadIconBtn
              type="button"
              aria-label="Histórico de comandos"
              title="Histórico de comandos"
              onClick={() => setHistoryOpen(true)}
            >
              {IHistory}
            </s.RailHeadIconBtn>
          </s.RailHeadIcons>
        )}
      </s.RailHead>

      {showCommandsTab ? (
        <Commands
          thing={thing}
          locked={locked}
          historyOpen={historyOpen}
          setHistoryOpen={setHistoryOpen}
        />
      ) : (
        <>
          {!isViewer && <s.Hint>Clique na variável para adicionar</s.Hint>}
          <s.Vars>
            {variables &&
              variables
                .filter(({ ihmname }) => typeof values[ihmname] !== 'undefined')
                .map((item) => {
                  const raw = values[item.ihmname];
                  const { unit } = item.range[0];
                  return (
                    <s.VarRow
                      key={item.id}
                      data-style={styleFor(raw)}
                      onClick={
                        user.role !== 'visualizador'
                          ? () => handleAddVariable(item)
                          : undefined
                      }
                    >
                      <s.VarIco>{ISearch}</s.VarIco>
                      <s.VarName>{item.name}</s.VarName>
                      <s.VarVal>
                        {formatValue(raw)}
                        <span className="u">{unit}</span>
                      </s.VarVal>
                    </s.VarRow>
                  );
                })}
          </s.Vars>
        </>
      )}
    </s.Container>
  );
};

export default List;
